import { useState } from 'react'
import {
  Title, Group, Card, Stack, Text, TextInput, Textarea, Button,
  Table, Badge, ActionIcon, Modal, LoadingOverlay,
} from '@mantine/core'
import { IconPlus, IconPencil, IconTrash, IconPhoto } from '@tabler/icons-react'
import { useForm, Controller } from 'react-hook-form'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { confirmDelete } from '@/lib/confirm'
import { useSvcCategories } from '@/lib/swr'
import type { SvcCategory } from '@/types'

interface FormData {
  name: string
  slug: string
  description: string
  icon: string
  color: string
  order: number
}

const EMPTY: FormData = {
  name: '', slug: '', description: '', icon: '', color: '#006C67', order: 0,
}

export default function SvcCategoryList() {
  const { data, isLoading, mutate } = useSvcCategories({ limit: '100' })
  const items: SvcCategory[] = data?.data ?? []

  const [modalOpen, setModalOpen] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [uploadingIcon, setUploadingIcon] = useState(false)

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ defaultValues: EMPTY })

  const watchIcon = watch('icon')

  const openCreate = () => { setEditId(null); reset(EMPTY); setModalOpen(true) }
  const openEdit = (item: SvcCategory) => {
    setEditId(item._id)
    reset({
      name: item.name, slug: item.slug, description: item.description,
      icon: item.icon, color: item.color, order: item.order,
    })
    setModalOpen(true)
  }
  const closeModal = () => { setModalOpen(false); reset(EMPTY) }

  const onSubmit = handleSubmit(async (form) => {
    try {
      const payload = { ...form, order: Number(form.order) || 0 }
      if (editId) await api.admin.updateSvcCategory(editId, payload)
      else await api.admin.createSvcCategory(payload)
      toast.success(editId ? 'Шинэчлэгдлээ' : 'Нэмэгдлээ')
      closeModal(); mutate()
    } catch (err: any) {
      toast.error(err instanceof Error ? err.message : 'Алдаа гарлаа')
    }
  })

  const handleDelete = (id: string) => confirmDelete('Энэ ангилалыг устгах уу?', async () => {
    try {
      await api.admin.deleteSvcCategory(id)
      toast.success('Устгагдлаа'); mutate()
    } catch (err: any) {
      toast.error(err instanceof Error ? err.message : 'Алдаа гарлаа')
    }
  })

  const handleIconUpload = async (file: File) => {
    setUploadingIcon(true)
    try {
      const result = await api.admin.uploadImage(file)
      setValue('icon', result.url, { shouldDirty: true })
    } catch { toast.error('Зураг байршуулахад алдаа гарлаа') }
    finally { setUploadingIcon(false) }
  }

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>Үйлчилгээний ангилал</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>Ангилал нэмэх</Button>
      </Group>

      <Card withBorder pos="relative">
        <LoadingOverlay visible={isLoading} />
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Icon</Table.Th>
              <Table.Th>Нэр</Table.Th>
              <Table.Th>Slug</Table.Th>
              <Table.Th>Өнгө</Table.Th>
              <Table.Th>Дараалал</Table.Th>
              <Table.Th w={100}>Үйлдэл</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.sort((a, b) => a.order - b.order).map((item) => (
              <Table.Tr key={item._id}>
                <Table.Td>
                  {item.icon ? (
                    <img src={item.icon} alt="" style={{ width: 28, height: 28, borderRadius: 6, objectFit: 'contain' }} />
                  ) : (
                    <div style={{ width: 28, height: 28, borderRadius: 6, background: item.color }} />
                  )}
                </Table.Td>
                <Table.Td>
                  <Text size="sm" fw={600}>{item.name}</Text>
                  {item.description && <Text size="xs" c="dimmed" lineClamp={1}>{item.description}</Text>}
                </Table.Td>
                <Table.Td><Badge variant="light" size="sm">{item.slug}</Badge></Table.Td>
                <Table.Td>
                  <Group gap={6}>
                    <div style={{ width: 16, height: 16, borderRadius: 4, background: item.color }} />
                    <Text size="xs">{item.color}</Text>
                  </Group>
                </Table.Td>
                <Table.Td><Text size="sm">{item.order}</Text></Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    <ActionIcon variant="light" size="sm" onClick={() => openEdit(item)}>
                      <IconPencil size={14} />
                    </ActionIcon>
                    <ActionIcon variant="light" color="red" size="sm" onClick={() => handleDelete(item._id)}>
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
            {items.length === 0 && !isLoading && (
              <Table.Tr><Table.Td colSpan={6}><Text ta="center" c="dimmed" py="lg">Ангилал бүртгэгдээгүй байна</Text></Table.Td></Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Card>

      <Modal opened={modalOpen} onClose={closeModal} title={editId ? 'Ангилал засах' : 'Шинэ ангилал'} size="md">
        <form onSubmit={onSubmit}>
          <Stack>
            <Controller
              name="name"
              control={control}
              rules={{ validate: (v) => (v.trim() ? true : 'Нэр шаардлагатай') }}
              render={({ field }) => <TextInput label="Нэр *" placeholder="жнь: Зүү эмчилгээ" {...field} error={errors.name?.message} />}
            />
            <Controller
              name="slug"
              control={control}
              rules={{ validate: (v) => (v.trim() ? true : 'Slug шаардлагатай') }}
              render={({ field }) => <TextInput label="Slug *" placeholder="жнь: acupuncture" {...field} error={errors.slug?.message} />}
            />
            <Controller
              name="description"
              control={control}
              render={({ field }) => <Textarea label="Тайлбар" {...field} minRows={2} />}
            />
            <Group grow>
              <Controller
                name="color"
                control={control}
                render={({ field }) => <TextInput label="Өнгө" placeholder="#006C67" {...field} />}
              />
              <Controller
                name="order"
                control={control}
                render={({ field }) => <TextInput label="Дараалал" type="number" value={String(field.value)} onChange={(e) => field.onChange(Number(e.target.value) || 0)} />}
              />
            </Group>
            <div>
              <Text size="sm" fw={500} mb={4}>Icon</Text>
              <Group>
                <Controller
                  name="icon"
                  control={control}
                  render={({ field }) => <TextInput flex={1} placeholder="URL" {...field} />}
                />
                <Button size="sm" variant="light" leftSection={<IconPhoto size={14} />} loading={uploadingIcon}
                  onClick={() => {
                    const input = document.createElement('input')
                    input.type = 'file'; input.accept = 'image/*'
                    input.onchange = () => input.files?.[0] && handleIconUpload(input.files[0])
                    input.click()
                  }}
                >Upload</Button>
              </Group>
              {watchIcon && <img src={watchIcon} alt="" style={{ marginTop: 8, maxHeight: 48, borderRadius: 8, objectFit: 'contain' }} />}
            </div>
            <Group justify="flex-end">
              <Button variant="light" onClick={closeModal} disabled={isSubmitting}>Болих</Button>
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>{editId ? 'Хадгалах' : 'Нэмэх'}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  )
}
